import React, { useEffect, useState, useCallback } from "react";
import { useI18n } from "../context/I18nContext";
import { api } from "../context/AuthContext";
import { dateFmt } from "../lib/format";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { EnvelopeSimple, ArrowsClockwise, ChatCircleDots, ArrowClockwise, CaretLeft, CaretRight } from "@phosphor-icons/react";

const PAGE = 25;
const statusStyle = {
  sent: "bg-emerald-100 text-emerald-700",
  failed: "bg-red-100 text-red-700",
  skipped: "bg-slate-100 text-slate-500",
};

const StatsChart = () => {
  const { t } = useI18n();
  const [days, setDays] = useState([]);
  useEffect(() => {
    api.get("/admin/notification-log/stats", { params: { days: 7 } }).then((r) => setDays(r.data.days || [])).catch(() => {});
  }, []);
  const max = Math.max(1, ...days.map((d) => d.sent + d.failed));
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5" data-testid="notif-stats">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-semibold text-emerald-900 text-sm">{t("notif_stats_title")}</h4>
        <div className="flex items-center gap-3 text-xs">
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-500" />{t("notif_sent")}</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-red-400" />{t("notif_failed")}</span>
        </div>
      </div>
      <div className="flex items-end justify-between gap-2 h-32">
        {days.map((d) => (
          <div key={d.date} className="flex-1 flex flex-col items-center gap-1.5" data-testid={`stat-day-${d.date}`}>
            <div className="w-full flex flex-col justify-end items-center gap-0.5" style={{ height: "100px" }}>
              {d.failed > 0 && <div className="w-6 rounded-t bg-red-400 transition-all" style={{ height: `${(d.failed / max) * 100}px` }} title={`${d.failed} ${t("notif_failed")}`} />}
              {d.sent > 0 && <div className={`w-6 bg-emerald-500 transition-all ${d.failed > 0 ? "" : "rounded-t"}`} style={{ height: `${(d.sent / max) * 100}px` }} title={`${d.sent} ${t("notif_sent")}`} />}
              {d.sent === 0 && d.failed === 0 && <div className="w-6 h-1 rounded bg-slate-100" />}
            </div>
            <span className="text-[10px] text-slate-400">{d.date.slice(5)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const NotificationLogPanel = () => {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [channel, setChannel] = useState("all");
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(0);
  const [resending, setResending] = useState("");

  useEffect(() => {
    const id = setTimeout(() => { setDebounced(search); setPage(0); }, 400);
    return () => clearTimeout(id);
  }, [search]);

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: PAGE, skip: page * PAGE };
    if (channel !== "all") params.channel = channel;
    if (status !== "all") params.status = status;
    if (debounced) params.search = debounced;
    api.get("/admin/notification-log", { params })
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [channel, status, debounced, page]);
  useEffect(load, [load]);
  useEffect(() => { setPage(0); }, [channel, status]);

  const resend = async (id) => {
    setResending(id);
    try { await api.post(`/admin/notification-log/${id}/resend`); toast.success(t("notif_resent"), { duration: 6000 }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error", { duration: 8000 }); }
    finally { setResending(""); }
  };

  const pages = Math.ceil(total / PAGE) || 1;

  return (
    <div className="space-y-5" data-testid="notifications-panel">
      <div className="flex items-center gap-2">
        <EnvelopeSimple size={22} weight="duotone" className="text-emerald-700" />
        <h3 className="font-display font-bold text-emerald-900 text-xl">{t("notifications_tab")}</h3>
      </div>
      <p className="text-sm text-slate-500">{t("notifications_hint")}</p>

      <StatsChart />

      <div className="flex flex-wrap items-center gap-3">
        <Input data-testid="notif-search" placeholder={t("search")} className="max-w-xs" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Select value={channel} onValueChange={setChannel}>
          <SelectTrigger data-testid="notif-channel" className="w-40 h-11"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("notif_all_channels")}</SelectItem>
            <SelectItem value="email">{t("notif_email")}</SelectItem>
            <SelectItem value="whatsapp">WhatsApp</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="notif-status" className="w-40 h-11"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("notif_all_status")}</SelectItem>
            <SelectItem value="sent">{t("notif_sent")}</SelectItem>
            <SelectItem value="failed">{t("notif_failed")}</SelectItem>
            <SelectItem value="skipped">{t("notif_skipped")}</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="notif-refresh" variant="outline" onClick={load} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 gap-1.5"><ArrowsClockwise size={16} weight="bold" /> {t("refresh")}</Button>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("notif_channel_col")}</TableHead>
              <TableHead>{t("notif_to")}</TableHead>
              <TableHead>{t("notif_subject")}</TableHead>
              <TableHead>{t("notif_status_col")}</TableHead>
              <TableHead>{t("notif_date")}</TableHead>
              <TableHead className="text-right">{t("actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">{t("loading")}</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8" data-testid="notif-empty">—</TableCell></TableRow>
            ) : items.map((i) => (
              <TableRow key={i.id} data-testid={`notif-row-${i.id}`}>
                <TableCell>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-800">
                    {i.channel === "whatsapp" ? <ChatCircleDots size={16} weight="duotone" /> : <EnvelopeSimple size={16} weight="duotone" />}
                    {i.channel === "whatsapp" ? "WhatsApp" : t("notif_email")}
                  </span>
                </TableCell>
                <TableCell className="text-sm text-slate-700">{i.to}</TableCell>
                <TableCell className="text-sm text-slate-600 max-w-md truncate" title={i.subject}>
                  {i.subject}
                  {i.status === "failed" && i.error ? <span className="block text-[11px] text-red-500 truncate">{i.error}</span> : null}
                </TableCell>
                <TableCell>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${statusStyle[i.status] || "bg-slate-100 text-slate-500"}`}>{t(`notif_${i.status}`) || i.status}</span>
                </TableCell>
                <TableCell className="text-xs text-slate-500">{dateFmt(i.created_at, lang)}</TableCell>
                <TableCell className="text-right">
                  {i.status === "failed" && i.resendable ? (
                    <Button size="sm" variant="ghost" data-testid={`notif-resend-${i.id}`} disabled={resending === i.id} onClick={() => resend(i.id)} className="text-emerald-700 hover:bg-emerald-50 gap-1 h-8">
                      <ArrowClockwise size={14} weight="bold" /> {resending === i.id ? "…" : t("notif_resend")}
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between text-sm text-slate-500">
        <span data-testid="notif-total">{total} {t("notif_records")}</span>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="notif-prev" className="h-8 gap-1"><CaretLeft size={14} /> {t("prev")}</Button>
          <span>{page + 1} / {pages}</span>
          <Button size="sm" variant="outline" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)} data-testid="notif-next" className="h-8 gap-1">{t("next")} <CaretRight size={14} /></Button>
        </div>
      </div>
    </div>
  );
};
